import { useEffect, useRef, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';

import { type DataTableSortStatus } from 'mantine-datatable';

// Task type still used by TasksTable sort state and TaskForm
import { IconX } from '@tabler/icons-react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { ActionIcon, Box, Button, Group, TextInput } from '@optiro/ui';

import { StandardPageLayout } from '@/components/StandardPageLayout';
import { NewTask, TaskForm } from '@/components/TaskForm';
import TasksTable from '@/components/TasksTable';
import useProjects from '@/hooks/projects/useProjects';
import { useCreateTask, useTaskSearch } from '@/hooks/useTasks';
import { useTaskDrawerStore } from '@/stores/useTaskDrawerStore';
import { Task } from '@/types/types';

export const Route = createFileRoute('/app/tasks/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    openTaskId: typeof search.openTaskId === 'string' ? search.openTaskId : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
});

type TaskSortColumn = 'name' | 'created_at' | 'estimated_cost';

function RouteComponent() {
  // --- State for search, sort, and pagination ---
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TaskSortColumn>('created_at');
  const { data: projects } = useProjects();
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const perPage = 25;

  const navigate = useNavigate();
  const { openTaskId, tab } = Route.useSearch();

  const globalTaskId = useTaskDrawerStore((s) => s.taskId);
  const openTaskDrawer = useTaskDrawerStore((s) => s.open);

  useEffect(() => {
    if (openTaskId) {
      openTaskDrawer(openTaskId, tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId, tab]);

  useEffect(() => {
    // The global drawer closed (e.g. via its own close button) while this
    // route's URL still points at it — clear the URL to match.
    if (globalTaskId === null && openTaskId) {
      navigate({ to: '/app/tasks', search: { openTaskId: undefined, tab: undefined } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTaskId, openTaskId]);

  // --- Infinite query ---
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useTaskSearch({
      search,
      sort,
      direction,
      perPage,
      projectIds: selectedProjects,
    });

  // --- Clear filters ---
  const clearFilters = () => {
    setSearch('');
    setSort('created_at');
    setDirection('desc');
    setSelectedProjects([]);
  };

  const handleRedirectToTask = (taskId: Task['id']) => {
    navigate({ to: '/app/tasks', search: { openTaskId: String(taskId), tab: undefined } });
  };

  const handleOpenAssignmentDrawer = (taskId: Task['id']) => {
    navigate({ to: '/app/tasks', search: { openTaskId: String(taskId), tab: undefined } });
  };

  const createTask = useCreateTask();
  const [opened, { close }] = useDisclosure(false);

  // --- Keep search input focused ---
  const searchInputRef = useRef<HTMLInputElement>(null);

  // --- Handle DataTable sort change ---
  const handleSortStatusChange = (sortStatus: DataTableSortStatus<Task>) => {
    if (!sortStatus) return;
    const accessor = (sortStatus.columnAccessor ?? 'created_at') as TaskSortColumn;
    const direction = (sortStatus.direction ?? 'desc') as 'asc' | 'desc';

    setSort(accessor);
    setDirection(direction);
    // refetch will happen automatically due to queryKey change
  };

  // --- Handle search change ---
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
    // Keep focus after update
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  // --- Flatten all tasks from pages ---
  const tasks = data?.pages.flatMap((page) => page.tasks) ?? [];

  const handleCreateTask = async (newTask: NewTask): Promise<Task['id'] | null> => {
    const result = await createTask.mutateAsync({
      ...newTask,
      project_milestone_id: newTask.project_milestone_id ?? undefined,
      project_id: newTask.project_id ?? undefined,
      estimated_cost: newTask.estimated_cost ?? undefined,
    });

    close();
    refetch();
    return result.data.id;
  };

  const handleQuickAddTask = async (name: string) => {
    await createTask.mutateAsync({ name });
  };

  // --- Prepare MultiSelect data for projects ---
  const projectOptions =
    projects?.map((p) => ({
      value: String(p.id),
      label: p.name,
    })) ?? [];

  return (
    <StandardPageLayout title={'Opgaver'} isError={!isLoading && tasks.length === 0}>
      <TaskForm opened={opened} onClose={close} onCreate={handleCreateTask} />
      <Group mb="md">
        <TextInput
          ref={searchInputRef}
          placeholder="Søg opgaver..."
          value={search}
          onChange={handleSearchChange}
          style={{ maxWidth: 300 }}
          rightSection={
            <ActionIcon size="xs" onClick={() => setSearch('')}>
              <IconX size={16} />
            </ActionIcon>
          }
        />

        <Button onClick={clearFilters}>Nulstil filtre</Button>
      </Group>
      <TasksTable
        tasks={tasks}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        onRedirectToTask={handleRedirectToTask}
        fetchNextPage={fetchNextPage}
        onSortStatusChange={handleSortStatusChange}
        sortStatus={{ columnAccessor: sort, direction }}
        projectOptions={projectOptions}
        selectedProjects={selectedProjects}
        setSelectedProjects={setSelectedProjects}
        onOpenAssignmentDrawer={handleOpenAssignmentDrawer}
        onCreateTask={handleQuickAddTask}
        columnVisibility={[
          'name',
          'status',
          'project',
          'milestone',
          'category',
          'trade',
          'location',
          'time_spent',
          'time_planned',
          'time_remaining',
          'resources',
          'created_at',
          'actions',
        ]}
        groupByOptions={['none', 'category', 'trade']}
      />
      {hasNextPage && (
        <Box ta="center" py="md">
          <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage} variant="light">
            Indlæs flere
          </Button>
        </Box>
      )}
    </StandardPageLayout>
  );
}
